import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity, AlertTriangle, ArrowUpRight, Ban, Calendar, CheckCircle2,
    ChevronDown, ChevronRight, Clock, Globe, Layers, Link as LinkIcon,
    Loader2, Network, Play, RefreshCw, Repeat, RotateCcw, SearchCode,
    Share2, Sparkles, Target, Trees,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";


const PRIORITY_STYLE = {
    high: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    medium: "text-neutral-200 bg-neutral-800 border-neutral-700",
    low: "text-text-secondary bg-white/5 border-white/10",
};

const ENGINE_META = {
    cluster_topic_engine: {
        icon: Trees, order: 1,
        title: "Block 1 · Clusters & Topics",
        subtitle: "1 LLM call · 3-5 clusters + 5-8 topic ideas (merged for budget)",
    },
    internal_linking_engine: {
        icon: Network, order: 3,
        title: "Block 3 · Internal Linking",
        subtitle: "Rules + 1 LLM · semantic graph over blog + landings + modules",
    },
    external_linking_engine: {
        icon: LinkIcon, order: 4,
        title: "Block 4 · External Linking",
        subtitle: "1 LLM · E-E-A-T outbound hints, zero HTTP calls",
    },
    backlinking_engine: {
        icon: Share2, order: 5,
        title: "Block 5 · Backlink Strategy",
        subtitle: "1 LLM · strategy only, NO outreach performed",
    },
    variant_wrapper: {
        icon: Sparkles, order: 6,
        title: "Block 6 · SEO Variants",
        subtitle: "Rules · wraps pending Optimization variants",
    },
    localization_wrapper: {
        icon: Globe, order: 7,
        title: "Block 7 · Localizations",
        subtitle: "Rules · wraps Recycling localizations (hreflang + geo)",
    },
    repurposing_wrapper: {
        icon: Repeat, order: 8,
        title: "Block 8 · Repurposings",
        subtitle: "Rules · schema markup + keyword coverage",
    },
    scheduling_wrapper: {
        icon: Calendar, order: 9,
        title: "Block 9 · Scheduling Hints",
        subtitle: "Rules · never writes to content_schedule",
    },
};


function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-neutral-200",
        secondary: "text-[var(--color-secondary)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}


function PriorityChip({ level }) {
    const cls = PRIORITY_STYLE[String(level || "low").toLowerCase()] || PRIORITY_STYLE.low;
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {level || "low"}
        </span>
    );
}


function SuggestionCard({ suggestion, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const status = suggestion.status || "pending";
    const isPending = status === "pending";
    const payload = suggestion.payload || {};

    const title = (
        payload.pillar_topic
        || payload.topic
        || payload.target_site_category
        || payload.seo_title_suggested
        || payload.anchor_text_suggested
        || payload.outbound_domain_hint
        || payload.target_language
        || payload.schema_markup_hint
        || payload.variant_type
        || payload.target_type
        || suggestion.engine_source
    );

    const act = async (kind) => {
        setBusy(true);
        try {
            const path = kind === "ack"
                ? `/seo-engine/acknowledge/${encodeURIComponent(suggestion.id)}`
                : `/seo-engine/dismiss/${encodeURIComponent(suggestion.id)}`;
            const body = kind === "dismiss"
                ? { reason: prompt("Dismiss reason (optional):") || "" }
                : {};
            await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
            toast.success(kind === "ack" ? "Acknowledged" : "Dismissed");
            onChanged?.();
        } catch (e) {
            toast.error(e.message || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <li
            data-testid={`seo-suggestion-${suggestion.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <PriorityChip level={suggestion.priority} />
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                            {suggestion.seo_dimension}
                        </span>
                        {suggestion.source_optimization_variant_id && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] flex items-center gap-1">
                                <Layers className="h-3 w-3" /> wraps optimization
                            </span>
                        )}
                        {suggestion.source_recycling_variant_id && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] flex items-center gap-1">
                                <Layers className="h-3 w-3" /> wraps recycling
                            </span>
                        )}
                        {suggestion.ai_generated && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> ai
                            </span>
                        )}
                        {status !== "pending" && (
                            <span className={`text-[10px] uppercase tracking-widest ${status === "acknowledged" ? "text-[var(--color-success)]" : "text-text-secondary"}`}>
                                · {status}
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-text-main font-medium truncate">
                        {String(title || "(no title)").slice(0, 140)}
                    </div>
                    {suggestion.reasoning && (
                        <p className="text-xs text-text-secondary italic mt-1 line-clamp-2">
                            {suggestion.reasoning}
                        </p>
                    )}
                    {suggestion.impact && (
                        <p className="text-[11px] text-text-secondary mt-1">
                            → {suggestion.impact}
                        </p>
                    )}
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`seo-payload-toggle-${suggestion.id}`}
                        className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] mt-2"
                    >
                        {expanded ? "Hide payload" : "Show payload"}
                    </button>
                    {expanded && (
                        <pre className="mt-2 whitespace-pre-wrap text-[11px] text-text-secondary bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-3 max-h-64 overflow-auto">
                            {JSON.stringify(payload, null, 2)}
                        </pre>
                    )}
                </div>
                {isPending && (
                    <div className="flex flex-col gap-1 shrink-0">
                        <button
                            data-testid={`seo-ack-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("ack")}
                            className="text-[10px] rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1 hover:bg-[var(--color-primary)]/10 disabled:opacity-50 flex items-center gap-1"
                        >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            Ack
                        </button>
                        <button
                            data-testid={`seo-dismiss-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("dismiss")}
                            className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 hover:text-text-main disabled:opacity-50 flex items-center gap-1"
                        >
                            <Ban className="h-3 w-3" />
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}


function BlockShell({ engineKey, engine, defaultOpen, onChanged, dimensionFilter,
                       titleOverride, subtitleOverride, iconOverride, orderOverride }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const meta = ENGINE_META[engineKey];
    if (!meta) return null;
    const Icon = iconOverride || meta.icon;
    const title = titleOverride || meta.title;
    const subtitle = subtitleOverride || meta.subtitle;
    const order = orderOverride || meta.order;
    const allSuggestions = engine?.suggestions || [];
    const suggestions = dimensionFilter
        ? allSuggestions.filter((s) => s.seo_dimension === dimensionFilter)
        : allSuggestions;
    const skipped = !!engine?.skipped;
    const priority = engine?.priority || "low";
    const impact = engine?.impact || "—";
    const testid = `seo-block-${order}`;

    return (
        <section
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`${testid}-toggle`}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-[var(--color-panel-ai)]/40 transition-colors"
            >
                <div className="flex items-start gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 grid place-items-center shrink-0">
                        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-text-main truncate">
                            {title}
                        </h3>
                        <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {skipped ? (
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                            skipped
                        </span>
                    ) : (
                        <>
                            <PriorityChip level={priority} />
                            <span className="text-xs text-text-secondary tabular-nums">
                                {suggestions.length} suggestions
                            </span>
                        </>
                    )}
                    {open ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
                </div>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-[var(--color-border)] p-5 md:p-6 space-y-3">
                            {skipped ? (
                                <p className="text-xs text-text-secondary italic">
                                    Engine skipped — {engine?.reason || "disabled via feature flag"}.
                                </p>
                            ) : (
                                <>
                                    {engine?.reasoning && (
                                        <p className="text-xs text-text-secondary italic">
                                            {engine.reasoning}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                                        <span>impact · {impact}</span>
                                        {engine?.risk && <span>· risk · {engine.risk}</span>}
                                        {engine?.duration_ms != null && (
                                            <span>· {engine.duration_ms}ms</span>
                                        )}
                                        {engine?.llm_used && <span>· llm ✓</span>}
                                    </div>
                                    {suggestions.length === 0 ? (
                                        <p className="text-xs text-text-secondary">
                                            No suggestions from this engine.
                                        </p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {suggestions.map((s) => (
                                                <SuggestionCard
                                                    key={s.id}
                                                    suggestion={s}
                                                    onChanged={onChanged}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}


export default function SeoEngineDashboard() {
    const { user: me } = useAuth();
    const [reports, setReports] = useState([]);
    const [currentId, setCurrentId] = useState(null);
    const [current, setCurrent] = useState(null);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const loadReports = useCallback(async () => {
        try {
            const r = await apiFetch("/seo-engine/reports?limit=10");
            setReports(r.items || []);
            if (!currentId && (r.items || []).length > 0) {
                setCurrentId(r.items[0].id);
            }
        } catch (e) {
            if (e.status === 503) {
                toast.error("SEO Engine is disabled — flip SEO_ENGINE_ENABLED in Admin → System");
            } else {
                toast.error(e.message || "Failed to list reports");
            }
        }
    }, [currentId]);

    const loadCurrent = useCallback(async (id) => {
        if (!id) { setCurrent(null); return; }
        try {
            const r = await apiFetch(`/seo-engine/run/${encodeURIComponent(id)}`);
            setCurrent(r);
        } catch (e) {
            toast.error(e.message || "Failed to load report");
        }
    }, []);

    const loadTotals = useCallback(async () => {
        try {
            const r = await apiFetch("/seo-engine/totals");
            setTotals(r || {});
        } catch (e) {
            if (e.status !== 503) console.error("totals err", e);
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([loadReports(), loadTotals()]);
            setLoading(false);
        })();
    }, [loadReports, loadTotals]);

    useEffect(() => { loadCurrent(currentId); }, [currentId, loadCurrent]);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await apiFetch("/seo-engine/run", { method: "POST", body: JSON.stringify({}) });
            toast.success(`Run complete · ${r?.totals?.suggestions_persisted || 0} suggestions · ${r?.llm_calls_count || 0} LLM calls`);
            setCurrentId(r.id);
            await Promise.all([loadReports(), loadTotals()]);
        } catch (e) {
            if (e.status === 429) {
                toast.error("Daily quota reached (5/day). Try again tomorrow.");
            } else if (e.status === 503) {
                toast.error("SEO Engine is disabled.");
            } else {
                toast.error(e.message || "Run failed");
            }
        } finally {
            setRunning(false);
        }
    };

    const errors = current?.errors || {};
    const errorCount = Object.keys(errors).length;

    const kpis = useMemo(() => ({
        clusters: (totals.clusters || 0) + (totals.topics || 0),
        internalLinks: totals.internal_links || 0,
        externalLinks: (totals.external_links || 0) + (totals.backlink_strategies || 0),
        pending: totals.pending || 0,
        acknowledged: totals.acknowledged || 0,
    }), [totals]);

    // Note: brief lists 9 UI blocks. Engine `cluster_topic_engine` produces
    // both dimension=cluster (block 1) and dimension=topic (block 2), so we
    // render it twice with a filter on `seo_dimension`.
    const engineOrder = [
        { key: "cluster_topic_engine", filter: "cluster", override: {
            order: 1, title: "Block 1 · Clusters",
            subtitle: "Pillar topics + child topics (from cluster_topic_engine)",
        } },
        { key: "cluster_topic_engine", filter: "topic", override: {
            order: 2, title: "Block 2 · New Topics",
            subtitle: "Topic-discovery suggestions (from cluster_topic_engine)",
        } },
        { key: "internal_linking_engine" },
        { key: "external_linking_engine" },
        { key: "backlinking_engine" },
        { key: "variant_wrapper" },
        { key: "localization_wrapper" },
        { key: "repurposing_wrapper" },
        { key: "scheduling_wrapper" },
    ];

    // Runtime meta override (block 1/2 alias for cluster_topic_engine)
    const metaFor = (item) => {
        if (item.override) {
            const base = ENGINE_META[item.key] || {};
            return {
                ...base, order: item.override.order,
                title: item.override.title, subtitle: item.override.subtitle,
                icon: item.override.order === 1 ? Trees : Target,
            };
        }
        return ENGINE_META[item.key];
    };

    if (!isAdminRole(me?.role)) {
        return (
            <div
                data-testid="seo-engine-forbidden"
                className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"
            >
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                    data-testid="seo-engine-dashboard"
                >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                SEO Engine · V10
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                                Meta-intelligence SEO advisor
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                Wraps Optimization, Recycling, Content Mesh, Scheduling
                                and V50 Diagnostics into 9-block SEO suggestions.
                                Human-in-the-loop: approve = acknowledged (never mutates
                                content, links, schema or outbound requests).
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a
                                href="/admin/governance"
                                data-testid="seo-back-governance"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1"
                            >
                                Governance <ArrowUpRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={() => { loadReports(); loadTotals(); loadCurrent(currentId); }}
                                data-testid="seo-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                            <button
                                onClick={runNow}
                                disabled={running}
                                data-testid="seo-run-now"
                                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                                Run SEO Engine now
                            </button>
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-6">
                        <KpiCard icon={Trees} label="Clusters + topics" value={kpis.clusters}
                                  tone="primary" testid="seo-kpi-clusters" />
                        <KpiCard icon={Network} label="Internal links" value={kpis.internalLinks}
                                  tone="secondary" testid="seo-kpi-internal-links" />
                        <KpiCard icon={LinkIcon} label="Ext links + backlinks" value={kpis.externalLinks}
                                  tone="primary" testid="seo-kpi-external-links" />
                        <KpiCard icon={SearchCode} label="Pending suggestions" value={kpis.pending}
                                  tone="warning" testid="seo-kpi-pending" />
                        <KpiCard icon={CheckCircle2} label="Acknowledged" value={kpis.acknowledged}
                                  tone="success" testid="seo-kpi-acknowledged" />
                    </div>

                    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
                        <aside className="lg:sticky lg:top-6 lg:self-start">
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                                <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                                    Report history
                                </div>
                                {loading ? (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                                    </div>
                                ) : reports.length === 0 ? (
                                    <p className="text-xs text-text-secondary">
                                        Click <span className="text-text-main">Run SEO Engine now</span> to start.
                                    </p>
                                ) : (
                                    <ul className="space-y-1 max-h-96 overflow-y-auto pr-1">
                                        {reports.map((r) => (
                                            <li key={r.id}>
                                                <button
                                                    onClick={() => setCurrentId(r.id)}
                                                    data-testid={`seo-report-item-${r.id}`}
                                                    className={`w-full text-left rounded-lg px-3 py-2 text-xs transition-colors ${
                                                        r.id === currentId
                                                            ? "bg-[var(--color-panel-ai)] text-text-main"
                                                            : "text-text-secondary hover:bg-[var(--color-panel-ai)]/60 hover:text-text-main"
                                                    }`}
                                                >
                                                    <div className="tabular-nums">{(r.generated_at || "").slice(0, 16).replace("T", " ")}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] uppercase tracking-widest">{r.triggered_by}</span>
                                                        <span className="text-[10px] text-[var(--color-primary)]">
                                                            · {r.totals?.suggestions_persisted || 0}
                                                        </span>
                                                        <span className="text-[10px] text-[var(--color-secondary)]">
                                                            · {r.llm_calls_count || 0} llm
                                                        </span>
                                                        {r.status !== "completed" && (
                                                            <span className="text-[10px] text-[var(--color-alert)]">· {r.status}</span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </aside>

                        <div className="space-y-4">
                            {!current ? (
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
                                    <SearchCode className="h-8 w-8 text-[var(--color-primary)] mx-auto mb-3" />
                                    <p className="text-sm text-text-secondary">
                                        Run the SEO Engine to generate a report.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                                        data-testid="seo-run-header">
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {(current.generated_at || "").slice(0, 19).replace("T", " ")} UTC
                                            </span>
                                            <span>· duration {current.duration_ms}ms</span>
                                            <span>· <span className="text-text-main">{current.llm_calls_count || 0}</span> LLM calls</span>
                                            <span>· trigger <span className="text-text-main">{current.triggered_by}</span></span>
                                            <span>· top priority <PriorityChip level={current.totals?.top_priority} /></span>
                                        </div>
                                        {errorCount > 0 && (
                                            <div className="mt-3 rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 px-3 py-2 text-xs text-[var(--color-alert)] flex items-start gap-2">
                                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="font-medium">Partial run — {errorCount} engine(s) failed</div>
                                                    <div className="opacity-80 truncate">
                                                        {Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {engineOrder.map((item, idx) => {
                                        const meta = metaFor(item);
                                        return (
                                            <BlockShell
                                                key={`${item.key}-${item.filter || "all"}`}
                                                engineKey={item.key}
                                                engine={current.engines?.[item.key]}
                                                dimensionFilter={item.filter}
                                                orderOverride={item.override?.order}
                                                titleOverride={item.override?.title}
                                                subtitleOverride={item.override?.subtitle}
                                                iconOverride={meta?.icon}
                                                defaultOpen={idx === 0}
                                                onChanged={() => { loadCurrent(current.id); loadTotals(); }}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
